import type { CellOverlay, TripDetails, TripStats } from "../types";

export const emptyTrip = (): TripDetails => ({
  positions: new Float32Array(),
  timestamps: new Uint32Array(),
  joys: new Float32Array(),
  flags: new Uint8Array(),
  count: 0,
  cursor: 0n,
  baseCursor: 0n,
  finished: false,
  delta: false,
  bounds: null,
});

export const emptyCells = (): CellOverlay => ({
  towers: [],
  pointIndices: new Uint32Array(),
  towerIndices: new Uint32Array(),
  dbm: new Int16Array(),
  levels: new Uint8Array(),
  sources: [],
  sourceIndices: new Uint16Array(),
  connection: new Uint8Array(),
  linkFlags: new Uint8Array(),
  quality: new Int16Array(),
  noise: new Int16Array(),
  age: new Uint32Array(),
  channels: new Uint32Array(),
  pci: new Uint16Array(),
  bands: new Uint16Array(),
  bandwidth: new Uint32Array(),
  cursor: 0n,
  baseCursor: 0n,
  basePointIndex: 0,
  delta: false,
});

export const emptyStats = (): TripStats => ({
  altitudes: new Float32Array(),
  accuracies: new Float32Array(),
  speeds: new Float32Array(),
  batteries: new Float32Array(),
  cn0: new Float32Array(),
  pressures: new Float32Array(),
  steps: new BigUint64Array(),
  satellitesVisible: new Uint8Array(),
  satellitesUsed: new Uint8Array(),
  count: 0,
  cursor: 0n,
  baseCursor: 0n,
  delta: false,
  finished: false,
});

function append<T extends ArrayBufferView & { length: number; set(values: T, offset?: number): void }>(
  a: T,
  b: T,
  result: T,
): T {
  result.set(a);
  result.set(b, a.length);
  return result;
}

export function mergeStats(current: TripStats, delta: TripStats): TripStats {
  if (!delta.delta) return delta;
  const count = current.count + delta.count;
  const f32 = (a: Float32Array, b: Float32Array) => append(a, b, new Float32Array(count));
  const u8 = (a: Uint8Array, b: Uint8Array) => append(a, b, new Uint8Array(count));
  return {
    altitudes: f32(current.altitudes, delta.altitudes),
    accuracies: f32(current.accuracies, delta.accuracies),
    speeds: f32(current.speeds, delta.speeds),
    batteries: f32(current.batteries, delta.batteries),
    cn0: f32(current.cn0, delta.cn0),
    pressures: f32(current.pressures, delta.pressures),
    steps: append(current.steps, delta.steps, new BigUint64Array(count)),
    satellitesVisible: u8(current.satellitesVisible, delta.satellitesVisible),
    satellitesUsed: u8(current.satellitesUsed, delta.satellitesUsed),
    count,
    cursor: delta.cursor,
    baseCursor: 0n,
    delta: false,
    finished: delta.finished,
  };
}

const towerKey = (tower: CellOverlay["towers"][number]) =>
  `${tower.radio}:${tower.mcc}:${tower.net}:${tower.area}:${tower.cell}`;
const sourceKey = (source: CellOverlay["sources"][number]) =>
  `${source.subscriptionId}:${source.slotIndex}:${source.carrierId}:${source.carrierName}`;

export function mergeCells(current: CellOverlay, delta: CellOverlay): CellOverlay {
  if (!delta.delta) return delta;
  const towers = [...current.towers],
    known = new Map(towers.map((tower, index) => [towerKey(tower), index]));
  const remap = delta.towers.map((tower) => {
    const key = towerKey(tower),
      found = known.get(key);
    if (found !== undefined) return found;
    const index = towers.length;
    towers.push(tower);
    known.set(key, index);
    return index;
  });
  const sources = [...current.sources],
    knownSources = new Map(sources.map((source, index) => [sourceKey(source), index]));
  const sourceRemap = delta.sources.map((source) => {
    const key = sourceKey(source),
      found = knownSources.get(key);
    if (found !== undefined) return found;
    const index = sources.length;
    sources.push(source);
    knownSources.set(key, index);
    return index;
  });
  const count = current.pointIndices.length + delta.pointIndices.length;
  const towerIndices = new Uint32Array(count);
  towerIndices.set(current.towerIndices);
  for (let i = 0; i < delta.towerIndices.length; i++)
    towerIndices[current.towerIndices.length + i] = remap[delta.towerIndices[i]!]!;
  const sourceIndices = new Uint16Array(count);
  sourceIndices.set(current.sourceIndices);
  for (let i = 0; i < delta.sourceIndices.length; i++)
    sourceIndices[current.sourceIndices.length + i] = sourceRemap[delta.sourceIndices[i]!]!;
  return {
    towers,
    sources,
    towerIndices,
    sourceIndices,
    pointIndices: append(current.pointIndices, delta.pointIndices, new Uint32Array(count)),
    dbm: append(current.dbm, delta.dbm, new Int16Array(count)),
    levels: append(current.levels, delta.levels, new Uint8Array(count)),
    connection: append(current.connection, delta.connection, new Uint8Array(count)),
    linkFlags: append(current.linkFlags, delta.linkFlags, new Uint8Array(count)),
    quality: append(current.quality, delta.quality, new Int16Array(count)),
    noise: append(current.noise, delta.noise, new Int16Array(count)),
    age: append(current.age, delta.age, new Uint32Array(count)),
    channels: append(current.channels, delta.channels, new Uint32Array(count)),
    pci: append(current.pci, delta.pci, new Uint16Array(count)),
    bands: append(current.bands, delta.bands, new Uint16Array(count)),
    bandwidth: append(current.bandwidth, delta.bandwidth, new Uint32Array(count)),
    cursor: delta.cursor,
    baseCursor: 0n,
    basePointIndex: 0,
    delta: false,
  };
}

export function appendTrip(current: TripDetails, delta: TripDetails): TripDetails {
  const count = current.count + delta.count;
  const positions = append(current.positions, delta.positions, new Float32Array(count * 2));
  const timestamps = append(current.timestamps, delta.timestamps, new Uint32Array(count));
  const joys = append(current.joys, delta.joys, new Float32Array(count));
  const flags = append(current.flags, delta.flags, new Uint8Array(count));
  const a = current.bounds,
    b = delta.bounds;
  const bounds = !a
    ? b
    : !b
      ? a
      : {
          minLongitude: Math.min(a.minLongitude, b.minLongitude),
          minLatitude: Math.min(a.minLatitude, b.minLatitude),
          maxLongitude: Math.max(a.maxLongitude, b.maxLongitude),
          maxLatitude: Math.max(a.maxLatitude, b.maxLatitude),
        };
  return {
    positions,
    timestamps,
    joys,
    flags,
    count,
    cursor: delta.cursor,
    baseCursor: 0n,
    finished: delta.finished,
    delta: false,
    bounds,
  };
}
